import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { BigButton } from '../components/Buttons';
import { BigButtonOutlined } from '../components/Buttons/buttons.styles';
import Publicity from '../components/Publicity';
import { Environment } from '../constants/environments';
import { EventTrackLabel } from '../constants/ga4';
import { ImagesApp } from '../constants/images';
import { useCartContext } from '../hooks/contexts/cart.context';
import { useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useUserContext } from '../hooks/contexts/user.context';
import useGuardian from '../hooks/useGuardian';
import { useMetaConversions } from '../hooks/useMetaConversions';
import * as Styles from '../styles/pages/styles.payment-confirmed';
import FacebookPixel from '../trackings/FacebookPixel';
import GoogleAnalytics from '../trackings/GoogleAnalytics';
import formatDate from '../utils/formateDate';

const PartiallyPaid = () => {
  const posthog = usePostHog();
  const { render } = useGuardian();
  const { translate } = useI18nContext();
  const { user } = useUserContext();
  const { supportedLocales, currentLocale : { locale } } = useI18nContext();
  const { cart, summaryData, transactionId, combosInCart } = useCartContext();
  const { event, couponApplied } = useEventContext();

  const { clientUrl } = useParams();

  if (!event || !cart || !user) return null;

  const handleAnalyticsTracking = () => {
    posthog.capture('purchase:success_page_view');
    const contentsToFbPixelTrack = summaryData.map((ticket) => ({
      id : String(ticket.id),
      quantity : ticket.quantity,
    }));

    const valueToFbPixelTrackWithTax = summaryData.reduce(
      (acc, ticket) => acc + (ticket.price + ticket.tax) * ticket.quantity,
      0
    );

    FacebookPixel.trackPurchase(
      valueToFbPixelTrackWithTax, contentsToFbPixelTrack, String(event?.id), String(event?.accepted_currencies)
    );

    const totalTax = summaryData.reduce((acc, ticket) => acc + ticket.tax, 0);
    GoogleAnalytics.trackEvent(
      EventTrackLabel.PURCHASE,
      {
        currency : String(event?.accepted_currencies),
        affiliation : String(clientUrl),
        transaction_id : transactionId,
        tax : totalTax,
        shipping : 0,
        items : summaryData.map((ticket) => ({
          item_id : String(event?.id),
          item_name : String(event?.title),
          coupon : couponApplied ? couponApplied.coupon.name : '',
          item_category : ticket.date,
          item_brand : ticket.group,
          item_variant : ticket.title,
          quantity : ticket.quantity,
          discount : couponApplied ? couponApplied.coupon.value_discount : 0,
          price : ticket.price,
        })),
        value : summaryData.reduce(
          (acc, ticket) => acc + ticket.accumulatedValueInCart,
          0
        ),
      },
    );
  };

  useEffect(() => {
    handleAnalyticsTracking();
  }, []);

  // Track Meta Conversions API PageView automatically
  useMetaConversions();

  return (
    render(
      <Styles.Wrapper>
        <Styles.Aside>
          <Styles.SquareWhite />
          <Styles.SquareImageEvent>
            <Styles.ImageEvent src={event.poster.large} />
          </Styles.SquareImageEvent>
        </Styles.Aside>

        <Styles.Main>
          <Styles.Container>
            <Styles.Title>
              {translate(['INFORMATION', 'FIRST_INSTALLMENT_PAYMENT_SUCCESS'])}
            </Styles.Title>
            <Styles.Subtitle>
              {translate(['INFORMATION', 'TICKETS_SECURED'])}
            </Styles.Subtitle>
            <Styles.ContainerInfo>
              <Styles.RowInfo>
                <img src={ImagesApp.svg.icInfoPayment} alt="" />
                <Styles.TextInfo>
                  {translate(['INFORMATION', 'IMPORTANT_INFORMATION'])}
                </Styles.TextInfo>
              </Styles.RowInfo>
              <Styles.RowInfo>
                <Styles.Dot>
                  .
                </Styles.Dot>
                <Styles.TextInfoPartial>
                  {translate(['INFORMATION', 'TICKET_RELEASED_AFTER_ALL_INSTALLMENTS'])}
                </Styles.TextInfoPartial>
              </Styles.RowInfo>
              <Styles.RowInfo>
                <Styles.Dot>
                  .
                </Styles.Dot>
                <Styles.TextInfoPartial>
                  {translate(['INFORMATION', 'INSTALLMENT_PAYMENT_DEADLINE'])}
                </Styles.TextInfoPartial>
              </Styles.RowInfo>
              <Styles.RowInfo>
                <Styles.Dot>
                  .
                </Styles.Dot>
                <Styles.TextInfoPartial>
                  {translate(['INFORMATION', 'PAYMENT_DELAY_PENALTY'])}
                </Styles.TextInfoPartial>
              </Styles.RowInfo>
              <Styles.RowInfo>
                <Styles.Dot>
                  .
                </Styles.Dot>
                <Styles.TextInfoPartial>
                  {translate(['INFORMATION', 'ACCESS_MONTHLY_PIX_IN_MY_PURCHASES'])}
                </Styles.TextInfoPartial>
              </Styles.RowInfo>
            </Styles.ContainerInfo>
            <Styles.EventTitle>
              {event.title}
            </Styles.EventTitle>
            <Styles.ContainerLocation>
              <img src={ImagesApp.svg.icLocation} alt="" />
              <Styles.Location>
                {`${event.place.name}, ${event.place.city} - ${event.place.state}`}
              </Styles.Location>
            </Styles.ContainerLocation>
            <Styles.TitleEventDate>
              {translate(['INFORMATION', 'EVENT_DATES'])}
            </Styles.TitleEventDate>
            <Styles.EventDate>
              {formatDate({
                dateString : cart?.[0]?.type?.[0]?.dates?.[0]?.datetime ||
                  combosInCart?.[0]?.comboDates?.[0]?.datetime,
                locale,
                supportedLocales
              })}
            </Styles.EventDate>
            <Styles.DetailsSendEmail>
              {translate(['INFORMATION', 'PURCHASE_DETAILS_SENT_TO'])}
            </Styles.DetailsSendEmail>
            <Styles.EmailText>
              {user.email}
            </Styles.EmailText>

            <Publicity />

          </Styles.Container>

          <Styles.ContainerButtons>
            <BigButtonOutlined onClick={() => {
              posthog.capture('purchase:success_page_show_events_click');
              window.location.href = Environment.SITE_URL;
            }}>
              {translate(['INFORMATION', 'SEE_MORE_EVENTS'])}
            </BigButtonOutlined>
            <BigButton onClick={() => {
              posthog.capture('purchase:success_page_show_tickets_click');
              window.location.href = Environment.WALLET_URL;
            }}>
              {translate(['INFORMATION', 'SEE_INSTALLMENTS'])}
            </BigButton>
          </Styles.ContainerButtons>
        </Styles.Main>
      </Styles.Wrapper>
    )

  );
};

export default PartiallyPaid;
